"use client";

import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";
import { ospThemes } from "@ospweb/sdk-ui";
import type { SaveGoalTrackerDisplayItem } from "./saveGoalTrackerSpecs";
import { SAVE_GOAL_ACTIVATE_COPY } from "./saveGoalActivateCardCopy";

export type SaveGoalCardPlanPreviewSectionProps = {
  moves: SaveGoalTrackerDisplayItem[];
  readOnly?: boolean;
  onViewDetails?: () => void;
  className?: string;
};

const PREVIEW_MOVE_COUNT = 2;

/**
 * Activate goal card plan preview — Figma 96097:352440–352458.
 */
export const SaveGoalCardPlanPreviewSection: React.FC<
  SaveGoalCardPlanPreviewSectionProps
> = ({ moves, readOnly = false, onViewDetails, className }) => {
  if (moves.length === 0) {
    return null;
  }

  const previewMoves = moves.slice(0, PREVIEW_MOVE_COUNT);
  const remainingCount = Math.max(0, moves.length - PREVIEW_MOVE_COUNT);

  return (
    <div
      className={cn("flex flex-col gap-4", className)}
      data-testid="save-goal-card-plan-preview"
    >
      <div
        className="flex items-center gap-2 rounded-[10px] bg-background_accent3_overlay10 px-3 py-2.5"
        data-testid="save-goal-card-moves-badge"
      >
        <OspIcons
          name="award"
          size={18}
          className="shrink-0 text-content_accent3_primary"
          color={ospThemes.colors.content_accent3_primary}
        />
        <p className="title_m m-0 text-content_accent3_primary">
          {SAVE_GOAL_ACTIVATE_COPY.movesBadge(moves.length)}
        </p>
      </div>

      <div
        className="flex flex-col gap-3"
        data-testid="save-goal-card-moves-list"
      >
        {previewMoves.map((move, index) => (
          <div
            key={move.id}
            className="flex items-center gap-2.5"
            data-testid={`save-goal-card-move-row-${index + 1}`}
          >
            <span
              className="flex size-[22px] shrink-0 items-center justify-center rounded-[11px] bg-content_brand_primary body_bold_s text-content_inverse_primary"
              aria-hidden
            >
              {index + 1}
            </span>
            <p className="title_m m-0 min-w-0 flex-1 text-content_primary">
              {move.title}
            </p>
          </div>
        ))}
      </div>

      {remainingCount > 0 || !readOnly ? (
        <div
          className={cn(
            "flex items-center justify-between",
            readOnly && "pointer-events-none",
          )}
          data-testid="save-goal-card-moves-footer"
        >
          {remainingCount > 0 ? (
            <p className="body_medium_s m-0 text-content_secondary">
              {SAVE_GOAL_ACTIVATE_COPY.moreMoves(remainingCount)}
            </p>
          ) : (
            <span />
          )}
          {!readOnly ? (
            <button
              type="button"
              className="flex items-center gap-0.5 body_medium_s text-content_tertiary"
              onClick={onViewDetails}
              data-testid="save-goal-card-view-details"
            >
              {SAVE_GOAL_ACTIVATE_COPY.viewDetails}
              <OspIcons
                name="chevron-right"
                size={16}
                color={ospThemes.colors.content_tertiary}
              />
            </button>
          ) : (
            <p className="body_medium_s m-0 text-content_tertiary">
              {SAVE_GOAL_ACTIVATE_COPY.viewDetails}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};
